import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import { getUserRole, hasAnyPermission, hasPermission } from '../common/permissionUtils';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { useLocation } from 'react-router-dom';

// Icons - Outlined style for Uber aesthetic
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LocalTaxiOutlinedIcon from '@mui/icons-material/LocalTaxiOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';

const drawerWidth = 280;

// Uber-inspired color palette
const theme = {
  bg: '#FFFFFF',
  bgHover: '#F7F7F7',
  bgActive: '#F0F0F0',
  text: '#000000',
  textSecondary: '#757575',
  border: '#E5E5E5',
  accent: '#000000',
};

export default function ResponsiveDrawer(props) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const currentLang = i18n.language;
  // Use Ge'ez script for Amharic and Tigrinya
  const logoText = (currentLang === 'am' || currentLang === 'ti') ? 'ፕሉክ' : 'PLUKK';
  const { window } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const { signOut } = api;
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const dispatch = useDispatch();
  const location = useLocation();

  // Track open/closed state of menu sections
  const [openSections, setOpenSections] = useState({
    trips: true,
    people: false,
    fleet: false,
    shiftData: false,
    finance: false,
    settings: false,
  });

  const LogOut = () => {
    dispatch(signOut());
  };

  const [role, setRole] = useState(null);

  useEffect(() => {
    if (auth.profile) {
      setRole(getUserRole(auth.profile));
    }
  }, [auth.profile]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const canAccessItem = (item) => {
    if (!role) return false;
    if (item.perm) {
      return item.action
        ? hasPermission(auth.profile, settings, item.perm, item.action)
        : hasAnyPermission(auth.profile, settings, item.perm);
    }
    return item.access ? item.access.includes(role) : true;
  };

  // Menu structure with categories
  const menuStructure = {
    // Top-level items (no category)
    topItems: [
      { name: t('home'), url: '/', icon: <HomeOutlinedIcon />, access: ['admin', 'fleetadmin', 'driver', 'customer'], perm: 'dashboard' },
      { name: t('dashboard_text'), url: '/dashboard', icon: <DashboardOutlinedIcon />, access: ['admin', 'fleetadmin'], perm: 'dashboard' },
      { name: 'TurPris', url: '/turpris/match', icon: <TravelExploreOutlinedIcon />, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
      { name: 'TurPris Admin', url: '/turpris/admin', icon: <AdminPanelSettingsOutlinedIcon />, access: ['admin'] },
    ],

    // Trips section
    trips: {
      label: t('trips') || 'Trips',
      icon: <HistoryOutlinedIcon />,
      access: ['admin', 'fleetadmin', 'driver', 'customer'],
      perm: 'bookings',
      items: [
        { name: t('booking_history'), url: '/bookings', icon: <HistoryOutlinedIcon />, access: ['admin', 'fleetadmin', 'driver', 'customer'], perm: 'bookings', action: 'read' },
        { name: t('addbookinglable'), url: '/addbookings', icon: <AddCircleOutlineIcon />, access: ['admin', 'fleetadmin', 'customer'], perm: 'bookings', action: 'create' },
      ]
    },

    // People section
    people: {
      label: t('users') || 'People',
      icon: <PeopleOutlinedIcon />,
      access: ['admin', 'fleetadmin'],
      perm: 'users',
      items: [
        { name: t('riders'), url: '/users/riders', icon: <PersonOutlinedIcon />, access: ['admin', 'fleetadmin'], perm: 'users', action: 'read' },
        { name: t('drivers'), url: '/users/drivers', icon: <BadgeOutlinedIcon />, access: ['admin', 'fleetadmin'], perm: 'users', action: 'read' },
        { name: t('fleetadmins'), url: '/users/fleetadmins', icon: <GroupsOutlinedIcon />, access: ['admin'], perm: 'users', action: 'update' },
        { name: t('alladmin'), url: '/users/admins', icon: <GroupsOutlinedIcon />, access: ['admin'], perm: 'users', action: 'update' },
      ]
    },

    // Fleet section
    fleet: {
      label: t('fleet') || 'Fleet',
      icon: <DirectionsCarOutlinedIcon />,
      access: ['admin', 'fleetadmin', 'driver'],
      perm: 'fleet',
      items: [
        { name: t('car_type'), url: '/cartypes', icon: <CategoryOutlinedIcon />, access: ['admin'], perm: 'fleet', action: 'update' },
        { name: t('cars'), url: '/cars', icon: <LocalTaxiOutlinedIcon />, access: ['admin', 'fleetadmin', 'driver'], perm: 'fleet', action: 'read' },
      ]
    },

    // Shift Data section (Taximeter)
    shiftData: {
      label: t('shift_data') || 'Shift Data',
      icon: <ScheduleOutlinedIcon />,
      access: ['admin', 'fleetadmin'],
      perm: 'reports',
      items: [
        { name: t('taxi_owners'), url: '/taxi-owners', icon: <BusinessOutlinedIcon />, access: ['admin', 'fleetadmin'], perm: 'reports', action: 'read' },
        { name: t('taxi_information'), url: '/taxi-information', icon: <LocalTaxiOutlinedIcon />, access: ['admin', 'fleetadmin'], perm: 'reports', action: 'read' },
        { name: t('income_shift_report'), url: '/income-shift-report', icon: <ReceiptLongOutlinedIcon />, access: ['admin', 'fleetadmin'], perm: 'reports', action: 'read' },
        { name: t('competitor_prices') || 'Competitor Prices', url: '/competitor-prices', icon: <CompareArrowsIcon />, access: ['admin', 'fleetadmin'], perm: 'reports', action: 'read' },
      ]
    },

    // Finance section
    finance: {
      label: t('finance') || 'Finance',
      icon: <AccountBalanceWalletOutlinedIcon />,
      access: ['admin', 'fleetadmin', 'driver', 'customer'],
      perm: 'finance',
      items: [
        { name: t('my_wallet_tile'), url: '/userwallet', icon: <AccountBalanceWalletOutlinedIcon />, access: ['driver', 'customer'], perm: 'finance', action: 'read' },
        { name: t('withdraws'), url: '/withdraws', icon: <PaymentsOutlinedIcon />, access: ['admin'], perm: 'finance', action: 'update' },
        { name: t('add_to_wallet'), url: '/addtowallet', icon: <AddCircleOutlineIcon />, access: ['admin'], perm: 'finance', action: 'create' },
        { name: t('driver_earning'), url: '/driverearning', icon: <AssessmentOutlinedIcon />, access: ['admin', 'fleetadmin'], perm: 'reports', action: 'read' },
        { name: t('earning_reports'), url: '/earningreports', icon: <AssessmentOutlinedIcon />, access: ['admin'], perm: 'reports', action: 'read' },
      ]
    },

    // Settings section
    settings: {
      label: t('settings_title') || 'Settings',
      icon: <SettingsOutlinedIcon />,
      access: ['admin', 'fleetadmin', 'driver', 'customer'],
      perm: 'settings',
      items: [
        { name: t('profile'), url: '/profile', icon: <AccountCircleOutlinedIcon />, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
        { name: t('settings_title'), url: '/settings', icon: <TuneOutlinedIcon />, access: ['admin'], perm: 'settings', action: 'read' },
        { name: t('storefront') || 'Storefront', url: '/storefront', icon: <StorefrontOutlinedIcon />, access: ['admin', 'fleetadmin', 'supervisor'], perm: 'settings', action: 'update' },
        { name: t('zones'), url: '/zones', icon: <MapOutlinedIcon />, access: ['admin'], perm: 'settings', action: 'update' },
        { name: t('promo'), url: '/promos', icon: <LocalOfferOutlinedIcon />, access: ['admin'], perm: 'settings', action: 'update' },
        { name: t('push_notification_title'), url: '/notifications', icon: <NotificationsOutlinedIcon />, access: ['admin'], perm: 'notifications', action: 'read' },
        { name: t('cancellation_reasons'), url: '/cancelreasons', icon: <CancelOutlinedIcon />, access: ['admin'], perm: 'settings', action: 'update' },
        { name: t('languagesettings'), url: '/languagesettings', icon: <LanguageOutlinedIcon />, access: ['admin'], perm: 'settings', action: 'update' },
        { name: t('app_info'), url: '/appinfo', icon: <InfoOutlinedIcon />, access: ['admin'], perm: 'settings', action: 'read' },
      ]
    },
  };

  // Check if user has access to any item in a section
  const hasAccessToSection = (section) => {
    if (!role) return false;
    if (section.perm && !hasAnyPermission(auth.profile, settings, section.perm)) return false;
    if (section.access && !section.access.includes(role)) return false;
    return section.items.some(item => {
      if (item.perm) {
        return item.action
          ? hasPermission(auth.profile, settings, item.perm, item.action)
          : hasAnyPermission(auth.profile, settings, item.perm);
      }
      return item.access ? item.access.includes(role) : true;
    });
  };

  // Check if current path is within a section
  const isInSection = (section) => {
    return section.items.some(item => location.pathname === item.url);
  };

  // Styles
  const listItemStyle = {
    borderRadius: 2,
    mx: 1,
    mb: 0.5,
    color: theme.text,
    '&.Mui-selected': {
      bgcolor: theme.bgActive,
      color: theme.accent,
      '&:hover': { bgcolor: theme.bgActive },
    },
    '&:hover': { bgcolor: theme.bgHover },
  };

  const subListItemStyle = {
    borderRadius: 2,
    mx: 1,
    mb: 0.25,
    pl: 4,
    color: theme.text,
    '&.Mui-selected': {
      bgcolor: theme.bgActive,
      color: theme.accent,
      fontWeight: 600,
      '&:hover': { bgcolor: theme.bgActive },
    },
    '&:hover': { bgcolor: theme.bgHover },
  };

  const sectionHeaderStyle = {
    borderRadius: 2,
    mx: 1,
    mb: 0.5,
    color: theme.text,
    '&:hover': { bgcolor: theme.bgHover },
  };

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: theme.bg, display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Box
          component={Link}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            textDecoration: 'none',
          }}
        >
          {/* Icon/Logo mark */}
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 900,
                color: '#fff',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
              }}
            >
              P
            </Typography>
          </Box>
          {/* Text logo */}
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 900,
              letterSpacing: -0.5,
              color: '#000',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
            }}
          >
            {logoText}
          </Typography>
        </Box>
      </Box>

      {/* User Profile Section */}
      {auth.profile && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box
            component={Link}
            to="/profile"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              textDecoration: 'none',
              color: 'inherit',
              p: 1.5,
              borderRadius: 2,
              bgcolor: theme.bgHover,
              '&:hover': { bgcolor: theme.bgActive },
            }}
          >
            <Avatar
              src={auth.profile.profile_image}
              sx={{ width: 40, height: 40, bgcolor: theme.accent }}
            >
              {auth.profile.firstName?.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: theme.text,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {auth.profile.firstName} {auth.profile.lastName}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: theme.textSecondary,
                  textTransform: 'capitalize',
                }}
              >
                {role || auth.profile.usertype}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Divider sx={{ mx: 2, borderColor: theme.border }} />

      {/* Navigation */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        {role && (
          <List disablePadding>
            {/* Top-level items */}
            {menuStructure.topItems.map((item, index) => (
              canAccessItem(item) && (
                <ListItem key={`top-${index}`} disablePadding>
                  <ListItemButton
                    component={Link}
                    to={item.url}
                    selected={location.pathname === item.url}
                    sx={listItemStyle}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: location.pathname === item.url ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            ))}

            {/* Trips Section */}
            {hasAccessToSection(menuStructure.trips) && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleSection('trips')}
                    sx={sectionHeaderStyle}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      {menuStructure.trips.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={menuStructure.trips.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isInSection(menuStructure.trips) ? 600 : 400,
                      }}
                    />
                    {openSections.trips ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={openSections.trips} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {menuStructure.trips.items.map((item, index) => (
                      canAccessItem(item) && (
                        <ListItem key={`trips-${index}`} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={item.url}
                            selected={location.pathname === item.url}
                            sx={subListItemStyle}
                          >
                            <ListItemText
                              primary={item.name}
                              primaryTypographyProps={{
                                fontSize: 13,
                                fontWeight: location.pathname === item.url ? 600 : 400,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      )
                    ))}
                  </List>
                </Collapse>
              </>
            )}

            {/* People Section */}
            {hasAccessToSection(menuStructure.people) && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleSection('people')}
                    sx={sectionHeaderStyle}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      {menuStructure.people.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={menuStructure.people.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isInSection(menuStructure.people) ? 600 : 400,
                      }}
                    />
                    {openSections.people ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={openSections.people} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {menuStructure.people.items.map((item, index) => (
                      canAccessItem(item) && (
                        <ListItem key={`people-${index}`} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={item.url}
                            selected={location.pathname === item.url}
                            sx={subListItemStyle}
                          >
                            <ListItemText
                              primary={item.name}
                              primaryTypographyProps={{
                                fontSize: 13,
                                fontWeight: location.pathname === item.url ? 600 : 400,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      )
                    ))}
                  </List>
                </Collapse>
              </>
            )}

            {/* Fleet Section */}
            {hasAccessToSection(menuStructure.fleet) && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleSection('fleet')}
                    sx={sectionHeaderStyle}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      {menuStructure.fleet.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={menuStructure.fleet.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isInSection(menuStructure.fleet) ? 600 : 400,
                      }}
                    />
                    {openSections.fleet ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={openSections.fleet} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {menuStructure.fleet.items.map((item, index) => (
                      canAccessItem(item) && (
                        <ListItem key={`fleet-${index}`} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={item.url}
                            selected={location.pathname === item.url}
                            sx={subListItemStyle}
                          >
                            <ListItemText
                              primary={item.name}
                              primaryTypographyProps={{
                                fontSize: 13,
                                fontWeight: location.pathname === item.url ? 600 : 400,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      )
                    ))}
                  </List>
                </Collapse>
              </>
            )}

            {/* Shift Data Section */}
            {hasAccessToSection(menuStructure.shiftData) && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleSection('shiftData')}
                    sx={sectionHeaderStyle}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      {menuStructure.shiftData.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={menuStructure.shiftData.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isInSection(menuStructure.shiftData) ? 600 : 400,
                      }}
                    />
                    {openSections.shiftData ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={openSections.shiftData} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {menuStructure.shiftData.items.map((item, index) => (
                      canAccessItem(item) && (
                        <ListItem key={`shiftData-${index}`} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={item.url}
                            selected={location.pathname === item.url}
                            sx={subListItemStyle}
                          >
                            <ListItemText
                              primary={item.name}
                              primaryTypographyProps={{
                                fontSize: 13,
                                fontWeight: location.pathname === item.url ? 600 : 400,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      )
                    ))}
                  </List>
                </Collapse>
              </>
            )}

            {/* Finance Section */}
            {hasAccessToSection(menuStructure.finance) && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleSection('finance')}
                    sx={sectionHeaderStyle}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      {menuStructure.finance.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={menuStructure.finance.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isInSection(menuStructure.finance) ? 600 : 400,
                      }}
                    />
                    {openSections.finance ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={openSections.finance} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {menuStructure.finance.items.map((item, index) => (
                      canAccessItem(item) && (
                        <ListItem key={`finance-${index}`} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={item.url}
                            selected={location.pathname === item.url}
                            sx={subListItemStyle}
                          >
                            <ListItemText
                              primary={item.name}
                              primaryTypographyProps={{
                                fontSize: 13,
                                fontWeight: location.pathname === item.url ? 600 : 400,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      )
                    ))}
                  </List>
                </Collapse>
              </>
            )}

            {/* Settings Section */}
            {hasAccessToSection(menuStructure.settings) && (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleSection('settings')}
                    sx={sectionHeaderStyle}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      {menuStructure.settings.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={menuStructure.settings.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isInSection(menuStructure.settings) ? 600 : 400,
                      }}
                    />
                    {openSections.settings ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={openSections.settings} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {menuStructure.settings.items.map((item, index) => (
                      canAccessItem(item) && (
                        <ListItem key={`settings-${index}`} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={item.url}
                            selected={location.pathname === item.url}
                            sx={subListItemStyle}
                          >
                            <ListItemText
                              primary={item.name}
                              primaryTypographyProps={{
                                fontSize: 13,
                                fontWeight: location.pathname === item.url ? 600 : 400,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      )
                    ))}
                  </List>
                </Collapse>
              </>
            )}
          </List>
        )}
      </Box>

      {/* Logout Button */}
      <Box sx={{ p: 2, borderTop: `1px solid ${theme.border}` }}>
        <ListItemButton
          onClick={LogOut}
          sx={{
            borderRadius: 2,
            color: theme.text,
            '&:hover': { bgcolor: theme.bgHover },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            <LogoutOutlinedIcon />
          </ListItemIcon>
          <ListItemText
            primary={t('logout')}
            primaryTypographyProps={{ fontSize: 14 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex', direction: isRTL === 'rtl' ? 'rtl' : 'ltr', height: '100vh', overflow: 'hidden' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: '#FFFFFF',
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: theme.text }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              color: theme.text,
              fontWeight: 600,
              fontSize: 18,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
          >
            {settings && settings.appName ? settings.appName : ''}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          container={container}
          variant="temporary"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: theme.bg,
              borderRight: `1px solid ${theme.border}`,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: theme.bg,
              borderRight: `1px solid ${theme.border}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          bgcolor: '#FAFAFA',
          height: '100vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar />
        {props.children}
      </Box>
    </Box>
  );
}
